// airjump-plugin/composables/useRpc.js
// Calls Supabase RPCs or REST endpoints directly (no chat-server dependency)
import { SUPABASE_URL, SUPABASE_ANON_KEY, sb, authFetch } from './useSupabase.js';

// Map old chat-server RPC paths to Supabase equivalents
const SUPABASE_RPC_MAP = {
  'qq-list': { fn: 'qq_list' },
  'portfolio-categories': { fn: 'portfolio_categories_list' },
  'calculate-burbuja': { fn: 'calculate_burbuja', mapParams: (b) => ({ p_ancho: Math.min(b.ancho, b.largo), p_largo: Math.max(b.ancho, b.largo) }) },
  'toggle-autopilot': { fn: 'toggle_autopilot', mapParams: (b) => ({ p_phone: b.phone, p_active: b.active }) },
  'wa-command': { fn: 'wa_command_insert', mapParams: (b) => ({ p_conversation_id: b.conversation_id, p_command: b.command, p_payload: b.payload || {} }) },
  'send-quotation': { fn: 'send_quotation_whatsapp', mapParams: (b) => ({ p_quotation_id: b.quotation_id, p_conversation_id: b.conversation_id, p_send_prices: b.send_prices ?? true, p_send_link: b.send_link ?? true }) },
  'create-quotation': { fn: 'create_quotation_with_client', mapParams: (b) => ({ p_qq_id: b.qq_id, p_phone: b.phone, p_conversation_id: b.conversation_id || null, p_variable_overrides: b.variable_overrides || b.variables || null }) },
  'create-price-list': { fn: 'create_price_list', mapParams: (b) => ({ p_phone: b.phone, p_conversation_id: b.conversation_id, p_qq_ids: b.qq_ids }) },
  'send-image': { fn: 'wa_command_insert', mapParams: (b) => ({ p_conversation_id: b.conversation_id, p_command: 'sendImageUrl', p_payload: { phone: b.phone, url: b.url, caption: b.caption } }) },
  'search-quotations': { fn: 'search_quotations', mapParams: (b) => ({ p_query: b.q || b }) },
  'link-quotation': { fn: 'link_quotation', mapParams: (b) => ({ p_conversation_id: b.conversation_id, p_quotation_id: b.quotation_id }) },
  'send-price-list': { fn: 'send_price_list_whatsapp', mapParams: (b) => ({ p_price_list_id: b.price_list_id, p_conversation_id: b.conversation_id || null, p_phone: b.phone || null }) },
  'delete-quotation': { fn: 'delete_quotation', mapParams: (b) => ({ p_quotation_id: b.quotation_id }) },
  'delete-price-list': { fn: 'delete_price_list', mapParams: (b) => ({ p_id: b.price_list_id }) },
};

// Supabase RPC call helper
async function sbRpc(fn, params = {}) {
  const res = await authFetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`RPC ${res.status}: ${text}`);
  }
  const text = await res.text();
  if (!text) return null;
  return JSON.parse(text);
}

// Pattern-based handlers for paths with query params or path params
const PATTERN_HANDLERS = [
  { match: /^crm-init\/(.+)$/, handler: (m) => sbRpc('crm_init', { p_phone: m[1] }) },
  { match: /^portfolio\?/, handler: (_, path) => {
    const params = new URLSearchParams(path.split('?')[1]);
    return sbRpc('portfolio_list', { p_category: params.get('category') || null, p_query: params.get('query') || null, p_limit: parseInt(params.get('limit')) || 50 });
  }},
  { match: /^search_clients\?/, handler: (_, path) => {
    const q = new URLSearchParams(path.split('?')[1]).get('q') || '';
    return sb(`clients?or=(first_name.ilike.*${q}*,last_name.ilike.*${q}*,phone.ilike.*${q}*,normalized_phone.ilike.*${q}*)&select=id,first_name,last_name,phone,normalized_phone,company:companies(name)&limit=10`);
  }},
  { match: /^qq-variables$/, handler: async (_, _path, body) => {
    if (!body?.qq_ids?.length) return [];
    const ids = body.qq_ids.join(',');
    return sb(`quick_quotation_price_templates?quick_quotation_id=in.(${ids})&select=quick_quotation_id,computo_id,variable_mapping`);
  }},
];

export async function rpc(path, { method, body, query } = {}) {
  // 1. Exact RPC map match
  const cleanPath = path.split('?')[0];
  const mapping = SUPABASE_RPC_MAP[cleanPath];
  if (mapping) {
    const params = mapping.mapParams ? mapping.mapParams(body || query || {}) : {};
    return sbRpc(mapping.fn, params);
  }

  // 2. Pattern-based handlers
  for (const { match, handler } of PATTERN_HANDLERS) {
    const m = path.match(match);
    if (m) return handler(m, path, body);
  }

  // 3. link_client (REST PATCH)
  if (cleanPath === 'link_client' && body) {
    return sb(`wa_conversations?id=eq.${body.conversation_id}`, {
      method: 'PATCH',
      body: { client_id: body.client_id },
    });
  }

  // 4. create_and_link_client
  if (cleanPath === 'create_and_link_client' && body) {
    let firstName = body.first_name;
    let lastName = body.last_name;

    // If no name provided, get from wa_conversations push_name
    if (!firstName && body.phone) {
      const convRows = await sb(`wa_conversations?phone=eq.${body.phone}&select=push_name&limit=1`);
      const pushName = convRows?.[0]?.push_name;
      if (pushName && pushName.trim()) {
        const parts = pushName.trim().split(/\s+/);
        firstName = parts[0];
        lastName = parts.length > 1 ? parts.slice(1).join(' ') : body.phone;
      } else {
        firstName = 'WhatsApp';
        lastName = body.phone;
      }
    }

    // Use existing RPC to create or find client
    const result = await sbRpc('create_or_find_client', {
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: body.phone,
    });
    const clientId = result?.id;

    // Link to conversation
    if (clientId && body.conversation_id) {
      await sb(`wa_conversations?id=eq.${body.conversation_id}`, {
        method: 'PATCH',
        body: { client_id: clientId },
        prefer: 'return=minimal',
      });
    }

    return { id: clientId };
  }

  // 5. Fallback: direct Supabase REST query
  return sb(path, { method, body });
}

export async function sendText(phone, text, conversationId) {
  // Send via wa_commands (worker picks up)
  // If no conversationId, look it up first
  let convId = conversationId;
  if (!convId) {
    const convs = await sb(`wa_conversations?phone=eq.${phone}&select=id&limit=1`);
    convId = convs?.[0]?.id;
  }
  if (!convId) throw new Error('No conversation found for phone');
  return sb('wa_commands', {
    method: 'POST',
    body: {
      conversation_id: convId,
      command: 'sendText',
      payload: { phone, text },
      status: 'pending',
    },
  });
}
